import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const rules = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "A number", test: (p) => /\d/.test(p) },
];

export default function AccountSettings() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const passed = rules.filter((r) => r.test(next));
  const allPass = passed.length === rules.length;
  const matches = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && allPass && matches && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(data.message || "Password changed successfully.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title="Account Settings"
      subtitle="Manage your sign-in credentials."
    >
      <div className="max-w-xl space-y-6">
        <div className="flex items-center gap-2 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-3 text-sm text-cyan-200">
          <ShieldCheck className="h-4 w-4" /> Changing your password signs out all other devices and sessions.
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-800 bg-[#0A1124] p-6 space-y-5" data-testid="change-password-form">
          <div className="flex items-center gap-2 text-white">
            <KeyRound className="h-5 w-5 text-cyan-300" />
            <h2 className="font-display text-lg font-semibold">Change Password</h2>
          </div>

          <div className="space-y-2">
            <Label htmlFor="current-password" className="text-slate-300">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="current-password-input"
              className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-slate-300">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="new-password-input"
              className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
            />
            <ul className="mt-2 grid grid-cols-2 gap-1.5" data-testid="password-rules">
              {rules.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-400" : "text-slate-500"}`}>
                    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {r.label}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-slate-300">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="confirm-password-input"
              className="h-11 bg-slate-900/50 border-slate-700 text-white focus-visible:ring-cyan-400"
            />
            {confirm.length > 0 && !matches && (
              <p className="text-xs text-rose-400" data-testid="password-mismatch">Passwords do not match.</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="change-password-submit"
            className="w-full h-11 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
}
